/**
 * @override
 * King Living QuantityPicker Component
 *
 * Enhanced QuantityPicker with custom styling to match design mockup:
 * - Enabled state: white background, gray border, brown plus button
 * - Disabled state: light gray background, muted colors
 * - Rounded container with minus, input, and plus buttons
 */

import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {Button, HStack, Input, useMultiStyleConfig, useNumberInput} from '@salesforce/retail-react-app/app/components/shared/ui';
import BaseQuantityPicker from '@salesforce/retail-react-app/app/components/quantity-picker';

/**
 * @override Enhanced QuantityPicker with custom styling
 */
const QuantityPicker = (props) => {
  const intl = useIntl();
  const productName = props.productName;
  const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
    ...props,
    // Defaults
    focusInputOnChange: false,
    onFocus: (e) => {
      const {onFocus} = props;

      // This is useful for mobile devices, this allows the user to pop open the keyboard and set the
      // new quantity with one click.
      e.target.select();

      // If there is a `onFocus` property define, call it with the event captured.

      onFocus?.call(this, e);
    },
  });

  const inc = getIncrementButtonProps({
    'variant': 'outline',
    'aria-label': intl.formatMessage(
      {
        defaultMessage: 'Increment Quantity for {productName}',
        id: 'product_view.label.assistive_msg.quantity_increment',
      },
      {productName}
    ),
  });
  const dec = getDecrementButtonProps({
    'variant': 'outline',
    'aria-label': intl.formatMessage(
      {
        defaultMessage: 'Decrement Quantity for {productName}',
        id: 'product_view.label.assistive_msg.quantity_decrement',
      },
      {productName}
    ),
  });
  const input = getInputProps({
    'maxWidth': '44px',
    'textAlign': 'center',
    'aria-label': intl.formatMessage({
      defaultMessage: 'Quantity',
      id: 'product_view.label.quantity',
    }),
  });

  // Accessibility improvements:
  // 1. Allow keyboard focus on the buttons - Chakra overrides values passed to get*ButtonProps()
  inc.tabIndex = '';
  dec.tabIndex = '';
  // 2. Allow Space or Enter key to trigger buttons
  // Hitting space/enter triggers a "click" event, but the component listens for "mousedown".
  // We can't reuse the buttons' onMouseDown handler, so instead we hijack the input's onKeyDown.
  // @ref https://github.com/chakra-ui/chakra-ui/blob/%40chakra-ui/react%402.7.0/packages/components/number-input/src/use-number-input.ts#L333-L334
  inc.onKeyDown = (evt) => {
    if (evt.key === ' ' || evt.key === 'Enter') {
      evt.key = 'ArrowUp';
      input.onKeyDown(evt);
    }
  };
  dec.onKeyDown = (evt) => {
    if (evt.key === ' ' || evt.key === 'Enter') {
      evt.key = 'ArrowDown';
      input.onKeyDown(evt);
    }
  };

  const {
    isDisabled: propIsDisabled = false,
    disabled: propDisabled = false,
    variant = 'default',
    size = 'md',
    ...restProps
  } = props;

  const isDisabled = propIsDisabled || propDisabled;
  const styles = useMultiStyleConfig('QuantityPicker', {
    isDisabled,
    variant,
    size,
  });

  return (
    <HStack {...styles.container} {...restProps}>
      <Button {...styles.decrementButton} data-testid="quantity-decrement" {...dec}>
        <FormattedMessage
          defaultMessage={'\u2212'} // HTML &minus;
          id="product_view.label.quantity_decrement"
        />
      </Button>
      <Input {...styles.input} {...input} />
      <Button {...styles.incrementButton} data-testid="quantity-increment" {...inc}>
        <FormattedMessage defaultMessage="+" id="product_view.label.quantity_increment" />
      </Button>
    </HStack>
  );
};

QuantityPicker.displayName = BaseQuantityPicker.displayName;
QuantityPicker.propTypes = {
  ...BaseQuantityPicker.propTypes,
  isDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  variant: PropTypes.oneOf(['default']), // Extend with more variants as needed
  size: PropTypes.oneOf(['md']), // Extend with more sizes as needed
};

export default QuantityPicker;
